import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock, Lock } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { AiInsightCard } from '../../components/ui/AiInsightCard';
import { Card } from '../../components/ui/Card';
import { IntroCallout } from '../../components/ui/IntroCallout';
import { api } from '../../lib/api';

interface ChecklistItem { key: string; label: string; count: number; ok: boolean; }
interface ChecklistResp { period: { year: number; month: number; startDate: string; endDate: string }; checklist: ChecklistItem[]; }

const KNOWN_KEYS = new Set(['ap_pending', 'ar_drafts', 'exp_submitted']);

export function ClosingPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [closeError, setCloseError] = useState<string | null>(null);

  const { data, isLoading } = useQuery<ChecklistResp>({
    queryKey: ['closing-checklist', wsId, year, month],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get(`/workspaces/${wsId}/closing/checklist?year=${year}&month=${month}`)).data,
  });

  const close = useMutation({
    mutationFn: async () =>
      (await api.post(`/workspaces/${wsId}/closing/close`, { year, month })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['closing-checklist', wsId] });
      qc.invalidateQueries({ queryKey: ['closing-periods', wsId] });
    },
    onError: (err: any) => setCloseError(err?.response?.data?.message ?? err?.message ?? 'Close failed'),
  });

  const allOk = !!data && data.checklist.length > 0 && data.checklist.every((i) => i.ok);
  const years = [now.getUTCFullYear() - 1, now.getUTCFullYear(), now.getUTCFullYear() + 1];
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const inputCn =
    'h-9 px-2 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm';

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-5xl">
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <h1 className="display text-2xl font-semibold">
            {t('closing.titleWithPeriod', { year, month: String(month).padStart(2, '0') })}
          </h1>
          <div className="flex items-center gap-2">
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inputCn}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inputCn}>
              {months.map((m) => (
                <option key={m} value={m}>
                  {String(m).padStart(2, '0')}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setCloseError(null);
                close.mutate();
              }}
              disabled={close.isPending || !allOk}
              title={!allOk ? t('closing.cannotCloseTooltip') : undefined}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium disabled:opacity-40 disabled:pointer-events-none"
            >
              <Lock size={14} />
              {t('closing.runClose')}
            </button>
          </div>
        </div>
        <p className="text-sm text-ink-500 mb-6">{t('closing.subtitle')}</p>
        <IntroCallout storageKey="closing" title={t('intro.closing.title')} body={t('intro.closing.body')} />
        {closeError && (
          <div className="mb-4 text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {closeError}
          </div>
        )}

        <Card>
          {isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {data?.checklist.map((item) => {
                const label = KNOWN_KEYS.has(item.key) ? t(`closing.checklistItems.${item.key}`) : item.label;
                return (
                  <li key={item.key} className="px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {item.ok ? (
                        <div className="h-6 w-6 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                          <Check size={14} />
                        </div>
                      ) : (
                        <div className="h-6 w-6 rounded-full bg-amber-50 text-warn-500 flex items-center justify-center">
                          <Clock size={14} />
                        </div>
                      )}
                      <span className={item.ok ? 'text-ink-500 line-through' : ''}>{label}</span>
                    </div>
                    <span className="num text-sm">{item.count}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="mt-6">
          <AiInsightCard
            agent="close_coach"
            title={t('aiOutput.cards.closeCoach')}
            prompt={t('aiOutput.prompts.closeCoach', { year, month: String(month).padStart(2, '0') })}
          />
        </div>
      </div>
    </AppShell>
  );
}
